import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { BookOpen, Loader2, Sparkles, Upload, FileText, RefreshCw, Download } from "lucide-react";
import { Link } from "react-router-dom";
import { useLang } from "@/lib/i18n";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Scripture-manifestations panel. Zero AI.
 *
 * Given a library tag (esoteric / spiritual / veda), this reads the
 * profile's natal chart from /api/scripture/manifestations, runs
 * MongoDB $text search inside the library docs carrying that tag,
 * and shows the top passages grouped by planet-sign query.
 *
 * If no PDF with that tag has been uploaded, shows an upload nudge
 * pointing to /library.
 */
export default function ScripturePanel({ profileId, tag, title, description }) {
  const { lang } = useLang();
  const [data, setData] = useState(null);
  const [availability, setAvailability] = useState(null);
  const [loading, setLoading] = useState(false);

  const checkAvailability = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/scripture/available-tags`);
      setAvailability(data[tag] || { available: false, documents: [] });
    } catch { /* ignore */ }
  }, [tag]);

  useEffect(() => { checkAvailability(); }, [checkAvailability]);

  const run = useCallback(async () => {
    if (!profileId) return;
    setLoading(true); setData(null);
    try {
      const { data } = await axios.get(
        `${API}/scripture/manifestations/${profileId}`,
        { params: { tag, per_group: 4, lang } },
      );
      setData(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Manifestation lookup failed");
    }
    setLoading(false);
  }, [profileId, tag, lang]);

  useEffect(() => {
    if (availability?.available && profileId) run();
  }, [availability, profileId, run, lang]);

  return (
    <div className="space-y-4" data-testid={`scripture-panel-${tag}`}>
      <div className="border-l-4 border-[#D4AF37] pl-3">
        <div className="text-xs uppercase tracking-widest text-[#D4AF37] font-mono">
          {title}
        </div>
        <div className="text-[10px] text-[#6E6B64] font-mono mt-1">
          {description}
        </div>
      </div>

      {/* Availability banner */}
      {availability && !availability.available && (
        <div className="border border-[#2A2D35] bg-[#151821] rounded-sm p-4"
             data-testid={`scripture-empty-${tag}`}>
          <div className="flex items-start gap-3">
            <Upload className="text-[#D4AF37] shrink-0 mt-0.5" size={16} />
            <div className="text-sm text-[#A19E95] leading-relaxed">
              No <span className="text-[#D4AF37]">{tag}</span>-tagged PDF has been uploaded yet.
              This tab will auto-activate the moment you upload the relevant PDF via the Library
              with the tag <span className="font-mono text-[#E6E1D6]">{tag}</span>.
              <div className="mt-2">
                <Link to="/library"
                      className="text-xs text-[#D4AF37] hover:underline flex items-center gap-1">
                  <BookOpen size={12} /> Open Library to upload →
                </Link>
              </div>
            </div>
          </div>
        </div>
      )}

      {availability?.available && (
        <>
          <div className="border border-[#2A2D35] bg-[#151821] rounded-sm p-3 flex items-center justify-between">
            <div className="text-xs text-[#A19E95]">
              Sources:{" "}
              {availability.documents.map((d) => (
                <span key={d.doc_id} className="mr-2 text-[#E6E1D6]">
                  <FileText size={10} className="inline mr-1 text-[#D4AF37]" />
                  {d.filename} ({d.chunk_count} chunks)
                </span>
              ))}
            </div>
            <Button size="sm" variant="outline" onClick={run} disabled={loading}
                    className="border-[#2A2D35] text-[#A19E95] hover:text-[#D4AF37] hover:bg-transparent"
                    data-testid={`scripture-refresh-${tag}`}>
              {loading ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />}
            </Button>
          </div>

          {loading && <div className="text-[#A19E95] text-sm font-mono">Reading your horoscope…</div>}

          {data && (
            <>
              {/* Chart-grounded readings (new format) */}
              {Array.isArray(data.readings) && data.readings.length > 0 ? (
                <>
                  <div className="flex items-center justify-between">
                    <div className="text-[10px] font-mono text-[#6E6B64]">
                      {data.readings.length} placement readings for{" "}
                      <span className="text-[#E6E1D6]">{data.profile_name}</span>{" "}
                      · Asc <span className="text-[#D4AF37]">{data.ascendant_sign}</span>
                    </div>
                    <Button size="sm" variant="outline"
                            onClick={() => window.open(`${API}/scripture/readings-pdf/${profileId}?tag=${tag}`, "_blank")}
                            className="border-[#D4AF37]/40 text-[#D4AF37] hover:text-[#0F1115] hover:bg-[#D4AF37]"
                            data-testid={`scripture-download-${tag}`}>
                      <Download size={12} className="mr-1" /> Download PDF
                    </Button>
                  </div>

                  <div className="space-y-4">
                    {data.readings.map((r, i) => (
                      <div key={i}
                           className="border border-[#2A2D35] bg-[#0F1218] rounded-sm p-4"
                           data-testid={`scripture-reading-${tag}-${r.placement_key}`}>
                        {/* Placement header */}
                        <div className="flex items-baseline gap-3 mb-3">
                          <span className="text-2xl text-[#D4AF37] leading-none">{r.icon}</span>
                          <div className="flex-1">
                            <div className="text-lg text-[#E6E1D6] font-medium">{r.title}</div>
                            <div className="text-[11px] text-[#8A857B] font-mono">{r.detail}</div>
                          </div>
                        </div>

                        {/* Numbered PREDICTIONS — extracted from THIS tab's PDFs */}
                        {Array.isArray(r.predictions) && r.predictions.length > 0 && (
                          <div className="mb-3">
                            <div className="text-[10px] uppercase tracking-widest text-[#D4AF37] font-mono mb-2">
                              Predictions from this scripture
                            </div>
                            <ol className="space-y-2">
                              {r.predictions.map((p) => (
                                <li key={p.n} className="flex gap-2 text-sm leading-relaxed">
                                  <span className="text-[#D4AF37] font-mono shrink-0 w-6 text-right">
                                    {p.n}.
                                  </span>
                                  <div className="flex-1">
                                    <div className="text-[#E6E1D6]">
                                      {p.text}
                                    </div>
                                    {p.source && (
                                      <div className="text-[10px] text-[#6E6B64] font-mono mt-0.5">
                                        — {p.source}
                                      </div>
                                    )}
                                  </div>
                                </li>
                              ))}
                            </ol>
                          </div>
                        )}

                        {/* Numbered GUIDANCE */}
                        {Array.isArray(r.guidance) && r.guidance.length > 0 && (
                          <div className="border-t border-[#2A2D35] pt-3">
                            <div className="text-[10px] uppercase tracking-widest text-[#D4AF37] font-mono mb-2">
                              Guidance
                            </div>
                            <ol className="space-y-1">
                              {r.guidance.map((g) => (
                                <li key={g.n} className="flex gap-2 text-sm leading-relaxed">
                                  <span className="text-[#8A857B] font-mono shrink-0 w-6 text-right">
                                    {g.n}.
                                  </span>
                                  <span className="text-[#C7C2B6]"
                                        dangerouslySetInnerHTML={{
                                          __html: (g.text || "").replace(/\*\*(.+?)\*\*/g,
                                            '<span class="text-[#E6E1D6]">$1</span>'),
                                        }} />
                                </li>
                              ))}
                            </ol>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </>
              ) : (
                <div className="border border-[#2A2D35] bg-[#151821] rounded-sm p-4 text-sm text-[#6E6B64] italic">
                  {data.message || "No readings could be generated. Ensure the Charts tab is opened for this profile first."}
                </div>
              )}
            </>
          )}
        </>
      )}
    </div>
  );
}
